// FAR-SWAP (far-bld-model w41, APP_FACADES §11 "The far model in the main
// view"): past a per-BUILDING switch distance the main view draws the bake's
// far shape (FBL1, buildings_far.bin, farplan.js parseFar) instead of the GLB
// building. Pure (no THREE, no fetch): the node probe and the client run the
// same numbers.
//
// The unit is a CLUSTER: one far shape (the FBL1 triangles of a GLB tile's run
// that share vertices: a roof and the walls dropped from its outline). A GLB
// triangle belongs to the far shape whose roof holds its centroid (xz), else
// to the one with the nearest far wall within BUF_M (far.py's own GLB → building
// rule); none: it is never swapped. Both models carry the cluster per vertex as aSwap
// (Int16 × 4: anchor x, anchor z in m, e in cm, R in m; e ≤ 0 = never):
//   S = e / (SWAP_PX · θ) + R          θ = the pass's pixel angle (rad)
// The GLB triangle collapses when d > S, the far shape shows when d > S − BAND_M
// (d = the eye's xz distance to the anchor), so at any eye the two are exact
// complements but for a BAND_M overlap the far shape loses by depth (farbld.js).
// P1 (owner seam rules): at d ≥ S every point of the cluster is ≥ d − R away,
// where e projects ≤ SWAP_PX pixels.
//
// e (m) = the cluster's largest deviation GLB vs far shape, measured per tile
// on the GLB soup (glbSwap), over every vertex of its GLB triangles:
//   - inside the far roof (xz): its height ABOVE the far roof there (dormers,
//     chimneys; below the roof it is inside the far volume: walls under the
//     eaves, floors);
//   - outside it: its xz distance to the nearest far WALL of the cluster
//     (balconies, galleries, eaves past the outline), capped at E_CAP_M;
//   - a white-tile vertical (fascia, door layers, galleries; §10): at most
//     alt · SWAP_PX / TRIM_PX — the trim rule (below) drops it whole first;
//   - + E_SLACK_M (the 1/16 m FBL1 and 1/64 m GLBQ position quanta).
// COLOUR (P1 / P2): the far shape is flat (per face: COLOR_0 × the atlas tile
// MEANS, far.py), the GLB textured. e is raised to tile · SWAP_PX / COL_PX
// (tile = the cluster's smallest facade repeat in m: one uv unit, a bay or a
// storey), so S ≥ tile / (COL_PX · θ): a facade tile spans ≤ COL_PX px at the
// switch, and the GLB's texel has faded to its tile mean (the box mip in
// linear light = far.py's PNG mean, atlasktx2.mjs) from tile / (2·COL_PX px · θ) =
// FADE0 · S on: the switch is flat colour to flat colour.
//
// TRIM DROP (§11's rule; trimEncode): a white-tile vertical whose smallest
// altitude projects under TRIM_PX pixels at its distance drops WHOLE. Its
// centroid (2 m cells) and altitude (4 cm steps) go into its own uv at install
// (the white tile is solid white: fract(uv) = 0.5 samples its centre, the
// derivatives are 0), the same on all three vertices: one decision a triangle.
// The drop: alt · 1/(TRIM_PX · θ) < d − 1.5 (far → small → dropped; the
// reverted w39 prototype had this inverted).

export const SWAP_PX = 0.5;       // P1: a switch only where GLB and far differ by < 0.5 px
export const TRIM_PX = 1.0;       // §11: a white-tile vertical under 1 px drops whole
export const COL_PX = 1;          // P1 colour: a switch only where a facade tile spans ≤ 1 px (the fade from 2 px: seam lab jet-flight, COL_PX 2 = +0.0022 FLIP)
export const FADE0 = 0.5;         // the facade texel fades to its tile mean over [FADE0 · S, S] (from 4 px a tile)
export const BAND_M = 0.5;        // both drawn in (S − BAND_M, S]: the far shape loses by depth
export const BUF_M = 1.5;         // far.py GLB_BUF_M: a GLB triangle this near a footprint is its building's
export const E_CAP_M = 3;         // a deviation past this is "far off": S past every cull on every class
export const E_SLACK_M = 0.05;    // the position quanta (FBL1 1/16 m, GLBQ 1/64 m)
const SEG_CELL = 4;               // the wall-segment grid (m)
const VERT_NY = 0.3;              // |normal.y| under this = a vertical face (buildings.js TRIM_GLSL)

// ?farbld=0 | 1 | trim | swap (the seam lab's arm): what the client runs.
// Absent / 1 = both rules; 0 = neither (the old picture).
export function swapArmOf(search) {
  const m = /[?&]farbld=([^&]*)/.exec(search || '');
  const v = m ? decodeURIComponent(m[1]) : '1';
  if (v === '0' || v === 'off') return { swap: false, trim: false };
  if (v === 'trim') return { swap: false, trim: true };
  if (v === 'swap') return { swap: true, trim: false };
  return { swap: true, trim: true };
}

// The pass's pixel angle (rad, vertical, at the view centre) from a
// perspective projection's [1][1] and the viewport height in px; 0 = not a
// perspective camera (no rule).
export function pixelAngle(projElements, vpH) {
  const e = projElements;
  if (!(vpH > 0) || e[11] !== -1 || !(e[5] > 0)) return 0;
  return 2 / (e[5] * vpH);
}

// The switch distance of one cluster for pixel angle θ (m; Infinity = never).
export function switchDist(eCm, R, theta) {
  if (!(eCm > 0) || !(theta > 0)) return Infinity;
  return (eCm * 0.01) / (SWAP_PX * theta) + R;
}

// FBL1 (parseFar) → { tiles: Map('tx_ty' → tile), aSwap: Int16Array(tris · 12) }
// tile = { key, tri0 (absolute), ntris, parts: [{ x0, x1, z0, z1, y1, ax, az, R }],
//          cl: [{ …, e }] (clusters), pc: Int32Array (part → cluster),
//          triCl: Int32Array(ntris) (far triangle → part), roofs, segs (grids) }
// aSwap starts at e = 0 (the far shape never shows) until glbSwap measured e.
export function farClusters(F) {
  const tiles = new Map();
  const aSwap = new Int16Array(F.tris * 12);
  for (const c of F.chunks) {
    const ox = (c.cx + 0.5) * F.chunkM, oz = (c.cz + 0.5) * F.chunkM, q = F.q;
    for (const s of c.subs) {
      if (!s.ntris) continue;
      const t0 = c.tri0 + s.tri0, n = s.ntris;
      const t = tileClusters(F.pos, t0, n, ox, c.yBase, oz, q);
      t.key = s.key;
      tiles.set(s.key, t);
      writeFar(aSwap, t);
    }
  }
  return { tiles, aSwap };
}

// the far triangles of one tile run → its clusters
function tileClusters(P, t0, n, ox, oy, oz, q) {
  // 1. parts: union-find over shared (quantised, exact) vertices
  const par = new Int32Array(n);
  for (let i = 0; i < n; i++) par[i] = i;
  const find = (i) => { while (par[i] !== i) { par[i] = par[par[i]]; i = par[i]; } return i; };
  const seen = new Map();
  for (let i = 0; i < n; i++) {
    const b = (t0 + i) * 9;
    for (let v = 0; v < 3; v++) {
      const o = b + v * 3;
      const k = ((P[o] + 32768) * 65536 + (P[o + 1] + 32768)) * 65536 + (P[o + 2] + 32768);
      const j = seen.get(k);
      if (j === undefined) seen.set(k, i);
      else { const a = find(i), r = find(j); if (a !== r) par[a] = r; }
    }
  }
  // 2. part boxes (world m)
  const box = new Map();
  for (let i = 0; i < n; i++) {
    const r = find(i), b = (t0 + i) * 9;
    let B = box.get(r);
    if (!B) box.set(r, (B = { x0: Infinity, x1: -Infinity, z0: Infinity, z1: -Infinity, y1: -Infinity }));
    for (let v = 0; v < 3; v++) {
      const o = b + v * 3, x = ox + P[o] * q, y = oy + P[o + 1] * q, z = oz + P[o + 2] * q;
      if (x < B.x0) B.x0 = x; if (x > B.x1) B.x1 = x;
      if (z < B.z0) B.z0 = z; if (z > B.z1) B.z1 = z;
      if (y > B.y1) B.y1 = y;
    }
  }
  // 3. clusters = the parts (a far shape's roof + its walls share vertices)
  const partCl = new Map();
  const cl = [];
  for (const [r, B] of box) {
    partCl.set(r, cl.length);
    const ax = Math.round((B.x0 + B.x1) / 2), az = Math.round((B.z0 + B.z1) / 2);
    // R covers the box from the ROUNDED anchor (+ 0.5 m of rounding)
    const R = Math.ceil(Math.hypot(Math.max(ax - B.x0, B.x1 - ax), Math.max(az - B.z0, B.z1 - az)) + 0.5);
    cl.push({ x0: B.x0, x1: B.x1, z0: B.z0, z1: B.z1, y1: B.y1, ax, az, R, e: 0 });
  }
  const triCl = new Int32Array(n);          // far triangle → its part
  for (let i = 0; i < n; i++) triCl[i] = partCl.get(find(i));
  // 4. the far walls as xz segments (the two farthest-apart corners of a
  // vertical far triangle), gridded with their cluster
  const segs = new Map(), roofs = new Map();
  for (let i = 0; i < n; i++) {
    const b = (t0 + i) * 9;
    const x = [0, 1, 2].map((v) => ox + P[b + v * 3] * q), y = [0, 1, 2].map((v) => oy + P[b + v * 3 + 1] * q),
      z = [0, 1, 2].map((v) => oz + P[b + v * 3 + 2] * q);
    const ux = x[1] - x[0], uy = y[1] - y[0], uz = z[1] - z[0], vx = x[2] - x[0], vy = y[2] - y[0], vz = z[2] - z[0];
    const nx = uy * vz - uz * vy, ny = uz * vx - ux * vz, nz = ux * vy - uy * vx;
    const nl = Math.hypot(nx, ny, nz);
    if (!(nl > 0)) continue;
    if (Math.abs(ny) / nl >= VERT_NY) { addRoof(roofs, [x[0], z[0], x[1], z[1], x[2], z[2], triCl[i], y[0], y[1], y[2]]); continue; }
    let a = 0, c = 1, best = -1;
    for (const [p, r] of [[0, 1], [0, 2], [1, 2]]) {
      const dd = (x[p] - x[r]) ** 2 + (z[p] - z[r]) ** 2;
      if (dd > best) { best = dd; a = p; c = r; }
    }
    addSeg(segs, [x[a], z[a], x[c], z[c], triCl[i]]);
  }
  // until a GLB soup links them (glbSwap) every part is its own cluster
  const pc = new Int32Array(cl.length);
  for (let i = 0; i < pc.length; i++) pc[i] = i;
  return { tri0: t0, ntris: n, parts: cl, cl: cl.map((C) => ({ ...C })), pc, triCl, roofs, segs };
}

function addRoof(roofs, r) {
  const gx0 = Math.floor(Math.min(r[0], r[2], r[4]) / 16), gx1 = Math.floor(Math.max(r[0], r[2], r[4]) / 16);
  const gz0 = Math.floor(Math.min(r[1], r[3], r[5]) / 16), gz1 = Math.floor(Math.max(r[1], r[3], r[5]) / 16);
  for (let gx = gx0; gx <= gx1; gx++) for (let gz = gz0; gz <= gz1; gz++) {
    const k = gx * 65536 + gz;
    const L = roofs.get(k);
    if (L) L.push(r); else roofs.set(k, [r]);
  }
}

// the cluster a GLB triangle (centroid x, z) belongs to: the far shape whose
// roof (xz) holds the point — of several (stacked parts), the first; else the
// one with the nearest far wall within BUF_M (far.py's GLB_BUF_M rule); −1: none
function ownerOf(t, x, z) {
  const r0 = roofOwner(t, x, z);
  if (r0 >= 0) return r0;
  const L = t.segs.get(Math.floor(x / SEG_CELL) * 65536 + Math.floor(z / SEG_CELL));
  let id = -1, best = BUF_M;
  if (L) for (const s of L) {
    const d = segDist(s, x, z);
    if (d < best) { best = d; id = s[4]; }
  }
  return id;
}

// the part whose far roof (xz) holds (x, z), −1: none
function roofOwner(t, x, z) {
  const R = t.roofs.get(Math.floor(x / 16) * 65536 + Math.floor(z / 16));
  if (R) {
    for (const r of R) {
      const d1 = (x - r[2]) * (r[1] - r[3]) - (r[0] - r[2]) * (z - r[3]);
      const d2 = (x - r[4]) * (r[3] - r[5]) - (r[2] - r[4]) * (z - r[5]);
      const d3 = (x - r[0]) * (r[5] - r[1]) - (r[4] - r[0]) * (z - r[1]);
      if (!((d1 < 0 || d2 < 0 || d3 < 0) && (d1 > 0 || d2 > 0 || d3 > 0))) return r[6];
    }
  }
  return -1;
}

// the far roof's height at (x, z) under cluster `id` (the highest of its roof
// triangles there), NaN outside its roof
function roofY(t, pc, id, x, z) {
  const R = t.roofs.get(Math.floor(x / 16) * 65536 + Math.floor(z / 16));
  let y = NaN;
  if (R) for (const r of R) {
    if (pc[r[6]] !== id) continue;
    const den = (r[3] - r[5]) * (r[0] - r[4]) + (r[4] - r[2]) * (r[1] - r[5]);
    if (!den) continue;
    const a = ((r[3] - r[5]) * (x - r[4]) + (r[4] - r[2]) * (z - r[5])) / den;
    const b = ((r[5] - r[1]) * (x - r[4]) + (r[0] - r[4]) * (z - r[5])) / den;
    const c = 1 - a - b, eps = -1e-6;
    if (a < eps || b < eps || c < eps) continue;
    const h = a * r[7] + b * r[8] + c * r[9];
    if (!(h <= y)) y = h;
  }
  return y;
}

function segDist(s, x, z) {
  const dx = s[2] - s[0], dz = s[3] - s[1], l2 = dx * dx + dz * dz;
  let u = l2 > 0 ? ((x - s[0]) * dx + (z - s[1]) * dz) / l2 : 0;
  u = u < 0 ? 0 : u > 1 ? 1 : u;
  return Math.hypot(x - s[0] - u * dx, z - s[1] - u * dz);
}

function addSeg(segs, s) {
  const [x0, z0, x1, z1] = s;
  const gx0 = Math.floor((Math.min(x0, x1) - E_CAP_M) / SEG_CELL), gx1 = Math.floor((Math.max(x0, x1) + E_CAP_M) / SEG_CELL);
  const gz0 = Math.floor((Math.min(z0, z1) - E_CAP_M) / SEG_CELL), gz1 = Math.floor((Math.max(z0, z1) + E_CAP_M) / SEG_CELL);
  for (let gx = gx0; gx <= gx1; gx++) for (let gz = gz0; gz <= gz1; gz++) {
    const k = gx * 65536 + gz;
    const L = segs.get(k);
    if (L) L.push(s); else segs.set(k, [s]);
  }
}

// xz distance of (x, z) to the nearest far wall of cluster `id` (≤ E_CAP_M)
function wallDist(segs, pc, id, x, z) {
  const L = segs.get(Math.floor(x / SEG_CELL) * 65536 + Math.floor(z / SEG_CELL));
  let best = E_CAP_M;
  if (!L) return best;
  for (const s of L) {
    if (pc[s[4]] !== id) continue;
    const d = segDist(s, x, z);
    if (d < best) best = d;
  }
  return best;
}

// write one tile's clusters into the far aSwap (its triangles' vertices)
export function writeFar(aSwap, t) {
  for (let i = 0; i < t.ntris; i++) {
    const C = t.cl[t.pc[t.triCl[i]]], e = C.e > 0 ? Math.min(32767, Math.ceil(C.e * 100)) : 0;
    const R = Math.min(32767, C.R);
    for (let v = 0; v < 3; v++) {
      const o = ((t.tri0 + i) * 3 + v) * 4;
      aSwap[o] = C.ax; aSwap[o + 1] = C.az; aSwap[o + 2] = e; aSwap[o + 3] = R;
    }
  }
}

const isWhite = (U1, i, white) => Math.abs(U1[i * 2] - white[0]) < 2e-3 && Math.abs(U1[i * 2 + 1] - white[1]) < 2e-3;

// the smallest altitude of triangle t (soup, 9 floats a triangle) in m
function altitude(P, t) {
  const o = t * 9;
  const ux = P[o + 3] - P[o], uy = P[o + 4] - P[o + 1], uz = P[o + 5] - P[o + 2];
  const vx = P[o + 6] - P[o], vy = P[o + 7] - P[o + 1], vz = P[o + 8] - P[o + 2];
  const A2 = Math.hypot(uy * vz - uz * vy, uz * vx - ux * vz, ux * vy - uy * vx);
  const e = Math.max(Math.hypot(ux, uy, uz), Math.hypot(vx, vy, vz), Math.hypot(vx - ux, vy - uy, vz - uz));
  return e > 0 ? A2 / e : 0;
}

// the world size (m) of one uv unit on facade triangle t (the smaller of
// |dP/du|, |dP/dv|): one repeat of its atlas tile; 0 = degenerate
function tileM(P, U, t) {
  const o = t * 9, q = t * 6;
  const e1x = P[o + 3] - P[o], e1y = P[o + 4] - P[o + 1], e1z = P[o + 5] - P[o + 2];
  const e2x = P[o + 6] - P[o], e2y = P[o + 7] - P[o + 1], e2z = P[o + 8] - P[o + 2];
  const du1 = U[q + 2] - U[q], dv1 = U[q + 3] - U[q + 1], du2 = U[q + 4] - U[q], dv2 = U[q + 5] - U[q + 1];
  const det = du1 * dv2 - du2 * dv1;
  if (!(Math.abs(det) > 1e-6)) return 0;
  const pu = Math.hypot(e1x * dv2 - e2x * dv1, e1y * dv2 - e2y * dv1, e1z * dv2 - e2z * dv1) / Math.abs(det);
  const pv = Math.hypot(e2x * du1 - e1x * du2, e2y * du1 - e1y * du2, e2z * du1 - e1z * du2) / Math.abs(det);
  return Math.min(pu, pv);
}

// the atlas WHITE tile of a soup (buildings.js _whiteOf's rule): the uv1 most
// up-facing vertices carry (a sample of ≤ 4096); null under 16 of them
export function whiteOf(N, U1) {
  if (!U1) return null;
  const n = N.length / 3, st = Math.max(1, Math.floor(n / 4096)), cnt = new Map();
  for (let i = 0; i < n; i += st) {
    if (N[i * 3 + 1] < 0.7) continue;
    const k = Math.round(U1[i * 2] * 4096) * 8192 + Math.round(U1[i * 2 + 1] * 4096);
    cnt.set(k, (cnt.get(k) || 0) + 1);
  }
  let best = -1, bn = 15;
  for (const [k, c] of cnt) if (c > bn) { best = k; bn = c; }
  return best >= 0 ? [Math.floor(best / 8192) / 4096, (best % 8192) / 4096] : null;
}

// the pool's half (pool-glb.js, after the cell sort): the soup's aSwap + its
// trims encoded into uv0 (in place) + the tile's clusters for the far side.
// t = the far tile (farClusters; null: no far shape here → no aSwap, trims only)
export function swapSoup(s, t, white) {
  const w = white || whiteOf(s.normal, s.uv1);
  let g = null;
  if (t) g = glbSwap(s.position, s.normal, s.uv0, s.uv1, w, t);      // before trimEncode: it reads uv0
  const trims = s.uv0 ? trimEncode(s.position, s.normal, s.uv0, s.uv1, w) : 0;
  return { aSwap: g ? g.aSwap : null, cl: t ? t.cl : null, pc: t ? t.pc : null, white: w, trims, assigned: g ? g.n : 0 };
}

// One GLB tile's soup (world-coordinate, non-indexed: P position, N normal,
// U1 uv1; white = the atlas white tile [u, v] or null) against its far tile `t`
// → its aSwap (Int16Array(count · 4)); sets t.cl / t.pc (the clusters) and
// their e. A cluster = the far parts one GLB triangle spans (a vertex of it
// clearly outside its owner part — not in its roof, > LINK_M from its walls
// — inside another part's roof links the two: one building's roof parts that
// share no far vertex). A triangle no far shape owns keeps aSwap 0 (never
// swapped); a cluster no GLB triangle reached keeps e 0 (its far shape never shows).
const LINK_M = 0.25;
export function glbSwap(P, N, U0, U1, white, t) {
  const nt = (P.length / 9) | 0, np = t.parts.length;
  const ident = new Int32Array(np);
  for (let i = 0; i < np; i++) ident[i] = i;
  // 1. the owner part of every triangle (centroid)
  const own = new Int32Array(nt);
  for (let i = 0; i < nt; i++) {
    const o = i * 9;
    own[i] = ownerOf(t, (P[o] + P[o + 3] + P[o + 6]) / 3, (P[o + 2] + P[o + 5] + P[o + 8]) / 3);
  }
  // 2. the parts one triangle spans
  const par = ident.slice();
  const find = (i) => { while (par[i] !== i) { par[i] = par[par[i]]; i = par[i]; } return i; };
  for (let i = 0; i < nt; i++) {
    const p = own[i];
    if (p < 0) continue;
    for (let v = 0; v < 3; v++) {
      const o = i * 9 + v * 3, x = P[o], z = P[o + 2];
      if (roofY(t, ident, p, x, z) === roofY(t, ident, p, x, z)) continue;     // in its own roof
      if (wallDist(t.segs, ident, p, x, z) <= LINK_M) continue;
      const q = roofOwner(t, x, z);
      if (q >= 0 && q !== p) { const a = find(p), b = find(q); if (a !== b) par[a] = b; }
    }
  }
  // 3. the clusters: boxes, anchor, R
  const pc = new Int32Array(np), idOf = new Map(), cl = [];
  for (let i = 0; i < np; i++) {
    const r = find(i);
    let id = idOf.get(r);
    if (id === undefined) { idOf.set(r, (id = cl.length)); cl.push({ x0: Infinity, x1: -Infinity, z0: Infinity, z1: -Infinity, y1: -Infinity }); }
    pc[i] = id;
    const C = cl[id], B = t.parts[i];
    if (B.x0 < C.x0) C.x0 = B.x0; if (B.x1 > C.x1) C.x1 = B.x1;
    if (B.z0 < C.z0) C.z0 = B.z0; if (B.z1 > C.z1) C.z1 = B.z1;
    if (B.y1 > C.y1) C.y1 = B.y1;
  }
  for (const C of cl) {
    C.ax = Math.round((C.x0 + C.x1) / 2); C.az = Math.round((C.z0 + C.z1) / 2);
    C.R = Math.ceil(Math.hypot(Math.max(C.ax - C.x0, C.x1 - C.ax), Math.max(C.az - C.z0, C.z1 - C.az)) + 0.5);
    C.e = -1;
    C.tile = Infinity;
  }
  // 4. e per cluster (module doc): inside the cluster's far roof only what
  // rises above it, outside the distance to its far walls
  for (let i = 0; i < nt; i++) {
    if (own[i] < 0) continue;
    const id = pc[own[i]], C = cl[id], o = i * 9;
    let dv = 0;
    for (let v = 0; v < 3; v++) {
      const x = P[o + v * 3], y = P[o + v * 3 + 1], z = P[o + v * 3 + 2];
      const ry = roofY(t, pc, id, x, z);
      const d = ry === ry ? Math.max(0, y - ry) : wallDist(t.segs, pc, id, x, z);
      if (d > dv) dv = d;
    }
    const wt = white && U1 && isWhite(U1, i * 3, white) && isWhite(U1, i * 3 + 1, white) && isWhite(U1, i * 3 + 2, white);
    if (dv > 0 && wt && Math.abs(N[o + 1]) < VERT_NY) {
      dv = Math.min(dv, altitude(P, i) * SWAP_PX / TRIM_PX);   // the trim rule drops it first
    }
    if (dv > C.e) C.e = dv;
    if (U0 && U1 && !wt) {                                     // a facade triangle: its tile's size
      const tm = tileM(P, U0, i);
      if (tm > 0 && tm < C.tile) C.tile = tm;
    }
  }
  for (const C of cl) {
    if (C.e < 0) { C.e = 0; continue; }
    C.e = Math.min(E_CAP_M, C.e) + E_SLACK_M;
    if (C.tile < Infinity) C.e = Math.max(C.e, C.tile * SWAP_PX / COL_PX);
  }
  t.cl = cl;
  t.pc = pc;
  // 5. the soup's aSwap
  const aSwap = new Int16Array(nt * 12);
  let n = 0;
  for (let i = 0; i < nt; i++) {
    if (own[i] < 0) continue;
    const C = cl[pc[own[i]]];
    const e = Math.min(32767, Math.ceil(C.e * 100)), R = Math.min(32767, C.R);
    for (let v = 0; v < 3; v++) { const q = (i * 3 + v) * 4; aSwap[q] = C.ax; aSwap[q + 1] = C.az; aSwap[q + 2] = e; aSwap[q + 3] = R; }
    n++;
  }
  return { aSwap, n };
}

// TRIM DROP encoding (module doc) into uv (U0) of every white-tile vertical
// triangle: uv.x = cell_x · 256 + alt_q + 0.5, uv.y = 32768 + cell_z + 0.5
// (cell = floor(centroid / 2 m), alt_q = ceil(alt / 4 cm) ≤ 255: 255 = never).
// Returns the triangles encoded. A centroid outside 0 … 32 km stays as is.
export function trimEncode(P, N, U0, U1, white) {
  if (!white || !U0 || !U1) return 0;
  const nt = (P.length / 9) | 0;
  let k = 0;
  for (let i = 0; i < nt; i++) {
    const o = i * 9;
    if (Math.abs(N[o + 1]) >= VERT_NY) continue;
    if (!isWhite(U1, i * 3, white) || !isWhite(U1, i * 3 + 1, white) || !isWhite(U1, i * 3 + 2, white)) continue;
    const cx = (P[o] + P[o + 3] + P[o + 6]) / 3, cz = (P[o + 2] + P[o + 5] + P[o + 8]) / 3;
    const hx = Math.floor(cx / 2), hz = Math.floor(cz / 2);
    if (hx < 0 || hz < 0 || hx > 16383 || hz > 16383) continue;
    const aq = Math.min(255, Math.ceil(altitude(P, i) / 0.04));
    const u = hx * 256 + aq + 0.5, w = 32768 + hz + 0.5;
    for (let v = 0; v < 3; v++) { U0[(i * 3 + v) * 2] = u; U0[(i * 3 + v) * 2 + 1] = w; }
    k++;
  }
  return k;
}
